import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Dropdown, DropdownChangeEvent, DropdownFilterEvent } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import orderServices from '../../services/orders/orderservice';
import RoleService from '../../services/user-role/role-service';
import { AssignRoleDTO, UpdateUserRoleDTO } from '../../models/user-role/role-model';

interface AssignRoleDialogProps {
    visible: boolean;
    onHide: () => void;
    onSaveSuccess: () => void;
    editMode: boolean;
    initialData?: { userId: number, roleId: number, userName: string };
    toast: React.RefObject<Toast>;
}

interface UserOption {
    userID: number;
    name: string;
    primaryContactNbr: string;
}

const AssignRoleDialog: React.FC<AssignRoleDialogProps> = ({ visible, onHide, onSaveSuccess, editMode, initialData, toast }) => {
    const blankUserOption = [{ userID: 0, detail: '', name: '', primaryContactNbr: '' }];
    const [users, setUsers] = useState<any[]>(blankUserOption);
    const [roles, setRoles] = useState<any[]>([]);
    const [selectedUser, setSelectedUser] = useState<number | null>(null);
    const [selectedRole, setSelectedRole] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);

    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (visible) {
            loadDropdowns();
            if (editMode && initialData) {
                setSelectedUser(initialData.userId);
                setSelectedRole(initialData.roleId);
                // Pre-fill user dropdown with the existing user so it displays correctly
                setUsers([{
                    userID: initialData.userId,
                    detail: initialData.userName, // Display name since we might not have mobile
                    name: initialData.userName,
                    primaryContactNbr: ''
                }]);
            } else {
                setSelectedUser(null);
                setSelectedRole(null);
                setUsers(blankUserOption);
            }
        }
    }, [visible, editMode, initialData]);

    const loadDropdowns = async () => {
        try {
            const rolesData = await RoleService.getAllRoles();
            setRoles(rolesData.data || rolesData);
        } catch (error) {
            console.error("Error loading dropdowns", error);
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load data', life: 3000 });
        }
    };

    const handleUserFilter = (e: DropdownFilterEvent) => {
        const query = e.filter;

        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(() => {
            if (query) {
                orderServices.getOrderUsers(query).then((response: UserOption[]) => {
                    if (response && response.length > 0) {
                        const formattedUsers = response.map(user => ({
                            detail: `${user.name}, ${user.primaryContactNbr}`,
                            userID: user.userID
                        }));
                        setUsers(formattedUsers);
                    }
                });
            }
        }, 300);
    };

    const handleSave = async () => {
        if (!selectedUser || !selectedRole) {
            toast.current?.show({ severity: 'warn', summary: 'Warning', detail: 'Please select both User and Role', life: 3000 });
            return;
        }

        setLoading(true);
        try {
            if (editMode) {
                const updateData: UpdateUserRoleDTO = {
                    userId: selectedUser,
                    newRoleId: selectedRole
                };
                const response = await RoleService.updateUserRole(updateData);
                if (response.isSuccess) {
                    toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Role updated successfully', life: 3000 });
                    onSaveSuccess();
                    onHide();
                } else {
                    toast.current?.show({ severity: 'error', summary: 'Error', detail: response.message || 'Failed to update role', life: 3000 });
                }
            } else {
                const assignData: AssignRoleDTO = {
                    userId: selectedUser,
                    roleId: selectedRole
                };
                const response = await RoleService.assignRoleToUser(assignData);
                if (response.isSuccess) {
                    toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Role assigned successfully', life: 3000 });
                    onSaveSuccess();
                    onHide();
                } else {
                    toast.current?.show({ severity: 'error', summary: 'Error', detail: response.message || 'Failed to assign role', life: 3000 });
                }
            }
        } catch (error: any) {
            console.error("Error saving role assignment", error);
            toast.current?.show({ severity: 'error', summary: 'Error', detail: error.message || 'An error occurred', life: 3000 });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            header={editMode ? "Update User Role" : "Assign Role to User"}
            modal
            className="assignment-dialog p-fluid w-30rem"
        >
            <div className="field ">
                <label htmlFor="user">User</label>
                <Dropdown
                    id="user"
                    value={selectedUser}
                    options={users}
                    onChange={(e) => setSelectedUser(e.value)}
                    onFilter={handleUserFilter}
                    optionLabel="detail"
                    optionValue="userID"
                    placeholder="Search for User/Mobile"
                    disabled={editMode}
                    filter
                    appendTo="self"
                />
            </div>
            <div className="field mt-2">
                <label htmlFor="role">Role</label>
                <Dropdown
                    id="role"
                    value={selectedRole}
                    options={roles}
                    onChange={(e) => setSelectedRole(e.value)}
                    optionLabel="name"
                    optionValue="roleId"
                    placeholder="Select a Role"
                    appendTo="self"
                />
            </div>
            <div className="flex justify-content-end mt-4 ">
                <Button
                    label="Save"
                    icon="pi pi-check"
                    onClick={handleSave}
                    loading={loading}
                    className="p-button-sm w-auto pr-2"
                />
            </div>
        </Dialog>
    );
};

export default AssignRoleDialog;
